import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Mail, Phone, Building2, UserCheck, X, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import { SearchBox } from "@/components/SearchBox";
import { ResetPasswordButton } from "@/components/ResetPasswordButton";
import { PortalAccessButton } from "@/components/PortalAccessButton";
import { InviteStatus, OwnerBulkBar } from "@/components/OwnerInvites";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Landlords() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [landlords, setLandlords] = useState([]);
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_channel: "email", address: "", notes: "", commission_pct: "" });

  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const isAdmin = user.role === "admin";
  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const load = () => api.get("/landlords", { params: { include_archived: showArchived, q } }).then((r) => setLandlords(r.data));
  const loadPending = () => { if (user.role === "admin") api.get("/users/pending").then((r) => setPending(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, [showArchived, q]);
  useEffect(() => { loadPending(); }, []);

  const canManage = ["admin", "operations"].includes(user.role);

  const approve = async (id) => {
    try { await api.post(`/users/${id}/approve`); toast.success("Owner approved"); loadPending(); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reject = async (id) => {
    try { await api.post(`/users/${id}/reject`); toast.success("Registration rejected"); loadPending(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const [editing, setEditing] = useState(null);
  const submit = async () => {
    try {
      const payload = { ...form, commission_pct: form.commission_pct === "" ? null : Number(form.commission_pct) };
      if (editing) { await api.put(`/landlords/${editing}`, payload); toast.success("Owner updated"); }
      else { await api.post("/landlords", payload); toast.success("Owner added"); }
      setOpen(false); setEditing(null);
      setForm({ name: "", email: "", phone: "", preferred_channel: "email", address: "", notes: "", commission_pct: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openEdit = (l) => { setEditing(l.id); setForm({ name: l.name || "", email: l.email || "", phone: l.phone || "", preferred_channel: l.preferred_channel || "email", address: l.address || "", notes: l.notes || "", commission_pct: l.commission_pct ?? "" }); setOpen(true); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-accent">CRM</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tight mt-1">Owners</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox testId="owners-search" placeholder="Search name, email, phone, address…" onSearch={setQ} />
          {canManage && (
          <Button variant="outline" size="sm" data-testid="toggle-archived-landlords" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? "Hide archived" : "Show archived"}
          </Button>
          )}
          {canManage && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild><Button data-testid="new-landlord-button" onClick={() => { setEditing(null); setForm({ name: "", email: "", phone: "", preferred_channel: "email", address: "", notes: "", commission_pct: "" }); }}><Plus className="h-4 w-4 mr-1" />New Owner</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-head">{editing ? "Edit Owner" : "Add Owner"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="overline">Name</Label><Input data-testid="ll-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="overline">Email</Label><Input data-testid="ll-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
                  <div><Label className="overline">Phone</Label><Input data-testid="ll-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
                </div>
                <div>
                  <Label className="overline">Preferred channel</Label>
                  <Select value={form.preferred_channel} onValueChange={(v) => setForm({ ...form, preferred_channel: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="overline">Commission % (optional override)</Label><Input data-testid="ll-commission-input" type="number" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} className="mt-1.5" placeholder="Uses global default if blank" /></div>
                <div><Label className="overline">Address</Label><Input data-testid="ll-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" /></div>
                <div><Label className="overline">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
                <p className="text-xs text-muted-foreground">Tip: use the owner's login email so they can see their own portfolio.</p>
              </div>
              <DialogFooter><Button data-testid="save-landlord-button" onClick={submit} disabled={!form.name}>{editing ? "Save changes" : "Add Owner"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {user.role === "admin" && pending.length > 0 && (
        <div className="bg-white border border-[#C9A24B]/40 rounded-md overflow-hidden" data-testid="pending-approvals-panel">
          <div className="px-4 py-3 bg-[#C9A24B]/10 border-b border-[#C9A24B]/30 flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-[#8A6D1E]" />
            <span className="overline font-semibold text-[#8A6D1E]">Pending owner approvals ({pending.length})</span>
          </div>
          <div className="divide-y divide-border">
            {pending.map((p) => (
              <div key={p.id} data-testid={`pending-row-${p.id}`} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="font-medium text-sm">{p.name}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{p.email}</span>
                    {p.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" data-testid={`reject-user-${p.id}`} onClick={() => reject(p.id)}><X className="h-4 w-4 mr-1" />Reject</Button>
                  <Button size="sm" data-testid={`approve-user-${p.id}`} onClick={() => approve(p.id)}><Check className="h-4 w-4 mr-1" />Approve</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-border rounded-md overflow-hidden">
        {isAdmin && sel.length > 0 && <div className="p-2"><OwnerBulkBar ids={sel} onClear={() => setSel([])} onDone={() => { setSel([]); load(); }} /></div>}
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/40 text-left">
              {isAdmin && <th className="pl-4 py-3 w-8"><Checkbox data-testid="select-all-owners" checked={landlords.length > 0 && sel.length === landlords.length} onCheckedChange={(c) => setSel(c ? landlords.map((l) => l.id) : [])} /></th>}
              <th className="px-4 py-3 overline font-semibold">Name</th>
              <th className="px-4 py-3 overline font-semibold">Contact</th>
              <th className="px-4 py-3 overline font-semibold">Channel</th>
              <th className="px-4 py-3 overline font-semibold">Portfolio</th>
              {canManage && <th className="px-4 py-3 overline font-semibold w-12"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {landlords.map((l) => (
              <tr key={l.id} data-testid={`landlord-row-${l.id}`} onClick={() => nav(`/landlords/${l.id}`)} className={`cursor-pointer hover:bg-secondary/40 transition-colors ${l.archived ? "opacity-60" : ""} ${sel.includes(l.id) ? "bg-[#C6A87C]/10" : ""}`}>
                {isAdmin && <td className="pl-4 py-3" onClick={(e) => e.stopPropagation()}><Checkbox data-testid={`select-owner-${l.id}`} checked={sel.includes(l.id)} onCheckedChange={() => toggleSel(l.id)} /></td>}
                <td className="px-4 py-3 font-medium">
                  {l.name}
                  {l.archived && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">Archived</span>}
                  {isAdmin && <div><InviteStatus owner={l} onDone={load} /></div>}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  <div className="flex flex-col gap-0.5">
                    {l.email && <span className="flex items-center gap-1 text-xs"><Mail className="h-3 w-3" />{l.email}</span>}
                    {l.phone && <span className="flex items-center gap-1 text-xs"><Phone className="h-3 w-3" />{l.phone}</span>}
                  </div>
                </td>
                <td className="px-4 py-3 capitalize text-muted-foreground">{l.preferred_channel}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-secondary"><Building2 className="h-3 w-3" />{l.portfolio_size} properties</span></td>
                {canManage && <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}><div className="flex items-center justify-end gap-1">{user.role === "admin" && (l.has_login ? <ResetPasswordButton endpoint={`/landlords/${l.id}/reset-password`} name={l.name} testId={`reset-pwd-owner-${l.id}`} /> : <PortalAccessButton owner={l} onDone={load} />)}<Button size="sm" variant="ghost" className="h-8 text-xs" data-testid={`edit-landlord-${l.id}`} onClick={() => openEdit(l)}>Edit</Button><RowActions entity="landlords" id={l.id} archived={l.archived} name={l.name} canDelete={user.role === "admin"} onDone={load} /></div></td>}
              </tr>
            ))}
          </tbody>
        </table>
        {landlords.length === 0 && <p data-testid="owners-empty" className="p-10 text-center text-sm text-muted-foreground">{q ? `No owners match "${q}".` : "No owners yet."}</p>}
      </div>
    </div>
  );
}
